import {React, useContext} from 'external/react'
import { exportYearRollbook } from 'components/gradebook/export'

import {
  styled, clsx, Box, Button, FormControlLabel, Checkbox, useMediaQuery, useTheme,
  Dialog, DialogActions, DialogContent, DialogTitle
} from 'external/material'

import CheckboxList from 'components/CheckBoxList'
import {useImmutableForm} from 'helpers/form-hook'
import {gradeMatchesPeriod} from 'helpers/gradebook'

import ToggleExportFormat from './ToggleExportFormat'


import {TeacherContext} from 'teacher/context'

const initialState = {
  includeIgnored: false,
  exportType: 'xlsx',
  rollbookLabel: '',
  periods: [],
  groups: []
}

const StyledDialog = styled(Dialog)(formStyles)

export default function ExportRollbookForm (props) {

  const {rollbookLabel} = props

  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const context = useContext(TeacherContext)
  const {state: form, updateState: updateForm, resetState: resetForm} = useImmutableForm({...initialState, rollbookLabel})

  const {groups, translate, updateState, rollbookContextGradingDialogs: gradingDialogs, rollbookContextPeriods: {items: periods}, rollbookContextGradesToExport: gradesToExport} = context

  const isOpen = periods.length > 0 && groups.length > 0 && gradingDialogs['export-rollbook']


  function closeDialog () {
    updateState({rollbookContextGradingDialogs: {'export-rollbook': {$set: false}}})
    resetForm()
  }

  function exportRollbook() {
    const filteredGroups = form.groups.length != groups.length
      ? groups.filter(g => form.groups.indexOf(g.id) >= 0)
      : groups 
    const exportData = form.periods.length != periods.length
      ? {...gradesToExport, gradeColumns: gradesToExport.gradeColumns.filter(column =>
          periods
            .filter(p => form.periods.indexOf(p.id) >= 0)
            .find(gradeMatchesPeriod(column))
        )}
      : gradesToExport
    exportYearRollbook(context, exportData, filteredGroups, rollbookLabel, form.exportType, form.includeIgnored)
    //closeDialog()
  }

  const canExport = form.groups.length > 0 && form.periods.length > 0

  return (
    <StyledDialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={closeDialog}
    >
      <DialogTitle className="title">{translate('export-rollbook-dialog-title', {rollbookLabel})}</DialogTitle>
      <DialogContent className="content-container">
        <Box className={clsx("row", "toggle-format")}>
          <ToggleExportFormat
            translate={translate}
            format={form.exportType}
            handleFormatChange={format => updateForm({exportType: {$set: format}})}
          />
        </Box>
        <Box className={clsx("row", "selection-lists")}>
          <Box className={clsx("periods", "selection-list")}>
            <CheckboxList
              mapLabel={g => g.properties.name}
              mapValue={g => g.id}
              checked={form.periods}
              options={periods}
              selectAllLabel={translate('all')}
              handleSelectAll ={selected => updateForm({periods: {$set: selected}})}
              handleToggle={selected => {
                const selectedIndex = form.periods.indexOf(selected) 
                const command = selectedIndex < 0
                  ? {$push: [selected]}
                  : {$splice: [[selectedIndex, 1]]}
                updateForm({periods: command})
              }}
              canSelectAll
            />
          </Box>
          <Box className={clsx("groups", "selection-list")}>
            <CheckboxList
              mapLabel={g => g.name}
              mapValue={g => g.id}
              checked={form.groups}
              options={groups}
              selectAllLabel={translate('all')}
              handleSelectAll ={selected => updateForm({groups: {$set: selected}})}
              handleToggle={selected => {
                const selectedIndex = form.groups.indexOf(selected) 
                const command = selectedIndex < 0
                  ? {$push: [selected]}
                  : {$splice: [[selectedIndex, 1]]}
                updateForm({groups: command})
              }}
              canSelectAll
            />
          </Box>
        </Box>
        <Box className={clsx("row", "include-ignored")}>
          <FormControlLabel
            control={<Checkbox color="primary" checked={form.includeIgnored} onChange={() => { updateForm({includeIgnored: {$apply: a => !a}})}} />}
            label={translate('export-rollbook-include-ignored-label')}
          />
        </Box>
      </DialogContent>
      <DialogActions className="actions">
        <Button
          variant="contained"
          className={clsx("button", "cancel")}
          onClick={closeDialog}>
            {translate('cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!canExport}
          className={clsx("button", "export")}
          onClick={exportRollbook}>
            {translate('export-rollbook')}
        </Button>
      </DialogActions>

    </StyledDialog>
  )
}

function formStyles() {

  return {
    "& .MuiPaper-root": {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 800,
      maxWidth: 1200,
      "& .title": {
        backgroundColor: '#efefefb8',
        borderBottom: 'solid 1px #ccc',
        fontSize: '14pt',
        fontWeight: 600,
        color: '#4c4c4c'
      },
      "& .actions": {
        padding: 20,
        "& .button": {
          textTransform: 'capitalize',
          "&.cancel": {
            marginRight: 10,
            backgroundColor: '#ccc',
            color: '#4e4848d9'
          }
        }
      },
      "& .content-container": {
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        "& .row": {
          padding: '0 20px',
          borderBottom: 'solid 1px #ccc',
          "& .MuiFormControlLabel-root": {
            marginLeft: 0
          },
          "& .MuiCheckbox-root": {
            padding: 0,
            margin: 0,
            marginRight: 10
          },
          "&.toggle-format": {
            padding: '15px 20px'
          },
          "&.include-ignored": {
            display: 'flex',
            padding: '15px 20px'
          },
          "&.selection-lists": {
            display: 'flex',
            flexGrow: 1,
            padding: 0,
            "& .selection-list": {
              flexGrow: 1,
              "& ul": {
                padding: '20px 0',
                "& li": {
                  padding: '5px 20px',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#efefef69'
                  },
                  '&.selected': {
                    backgroundColor: '#efefef69'
                  },
                  "& .MuiListItemIcon-root": {
                    minWidth: 20,
                  }
                }
              }
            }
          }
        },
      }
    },
  }

}
